import React from "react";
import { useCustomQuery } from "utilities/hookutils";
import { routes } from "utilities/routing/generatedRoutes";
import { UseModernOrLegacyHook } from "./hooks";
import ModernOrLegacy from "./ModernOrLegacy";
import SelectBookkeeping from "./SelectBookkeeping";
import { CurrencyAndExchangeRatePicker } from "components/exchangerate/CurrencyAndExchangeRatePicker";
import SelectBankAccount from "./SelectBankAccount";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import CostCenterModalPicker from "components/Picker/CostCenterPicker/CostCenterModalPicker";
import SelectBrand from "./SelectBrand";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import NoBankAccountsWarning from "./NoBankAccountsWarning";
import InvoiceV2CollectionMethodDropdown from "components/dropdowns/reactselect/InvoiceV2CollectionMethodDropdown";
import { ProductCheckboxList } from "../ProductCheckboxList";
import { ProjectCodeInput } from "components/ProjectCodeInput";
import { useModals } from "Modals/ModalsProvider";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import classNames from "classnames";

interface Props {
  bookkeepings?: server.dto.BookkeepingSmall[]
  brands?: server.dto.BrandSmall[]
  modernOrLegacyHook: UseModernOrLegacyHook;
  qs?: string;

  fromShow?: {
    currency: server.dto.Currencies;
    exchangeRate: number;
    addressee: server.dto.CompanySmall;
    costCenter: server.dto.CostCenter;
    projectCode: string;
    showId: number;
    promoterContactId: number;
  }
}

const CreateInvoice = (props: Props) => {
  const shouldStartLoadingData = props.modernOrLegacyHook.screen === "minimalCreationScreen";

  const brandsApi = useCustomQuery({
    queryFn: () => window.CoreApi.Brand.List({ page: 1, perPage: 100 }).then(r => r.data.data),
    queryKey: ["brands"],
    enabled: shouldStartLoadingData && !props.brands,
    initialData: props.brands ?? [],
    onSuccess: data => !brand && data.length === 1 && setBrand(data[0])
  });

  const bookkeepingApi = useCustomQuery({
    queryKey: ["bookkeeping"],
    queryFn: async () => window.CoreApi.Bookkeeping.PaginatedSmall({
      clientId: site.currentEnvironment.client.clientId,
      keyword: "",
      page: 1,
      perPage: 500,
      visible: "onlyYes",
    }, "limitByUserAccess").then(res => res.data.data),
    enabled: shouldStartLoadingData && !props.bookkeepings,
    initialData: props.bookkeepings ?? [],
    onSuccess: data => data.length === 1 && !bookkeeping && onBookkeepingChange(data[0])
  });

  const hasOnlyOneBrand = brandsApi.data.length === 1;
  const hasOnlyOneBookkeeping = bookkeepingApi.data.length === 1;

  const [brand, setBrand] = React.useState<server.dto.BrandSmall>(hasOnlyOneBrand ? brandsApi.data[0] : null);
  const [bookkeeping, setBookkeeping] = React.useState<server.dto.BookkeepingSmall>(hasOnlyOneBookkeeping ? bookkeepingApi.data[0] : null);
  const [bankAccount, setBankAccount] = React.useState<server.dto.BankAccountSmall>();
  const [addressee, setAddressee] = React.useState<server.dto.CompanySmall>(props.fromShow?.addressee);
  const [currency, setCurrency] = React.useState<server.dto.Currencies>(props.fromShow?.currency ?? "eur");
  const [exchangeRate, setExchangeRate] = React.useState<number>(props.fromShow?.exchangeRate ?? 1);
  const [costCenter, setCostCenter] = React.useState<server.dto.CostCenter>(props.fromShow?.costCenter);
  const [projectCode, setProjectCode] = React.useState<string>(props.fromShow?.projectCode ?? "");
  const [purchaseOrderNumber, setPurchaseOrderNumber] = React.useState<string>("");
  const [language, setLanguage] = React.useState<server.dto.LCID>(site.currentUser.culture);
  const [collectionMethod, setCollectionMethod] = React.useState<server.dto.InvoiceCollectionMethod>(serverReference.invoiceCollectionMethods.find(o => o.value === "bank"));

  const [hasFinishedInitializing, setHasFinishedInitializing] = React.useState(false);
  const [products, setProducts] = React.useState<number[]>([]);

  const bankAccountsApi = useCustomQuery({
    queryFn: () => window.CoreApi.BankAccount.ListBankAccountSmall({
      bookkeepings: bookkeeping != null ? [bookkeeping.bookkeepingId] : undefined,
      page: 1,
      perPage: 100
    }).then(r => r.data.data),
    queryKey: ["bankAccounts", { bookkeepingId: bookkeeping?.bookkeepingId }],
    enabled: shouldStartLoadingData,
    initialData: [],
    onSuccess: data => data.length === 1 && !bankAccount && setBankAccount(data[0])
  });

  const productListApi = useCustomQuery({
    queryFn: () => window.CoreApi.Product.ListByParent({
      mainArea: "show",
      parentId: props.fromShow?.showId
    }).then(r => r.data.data),
    enabled: props.fromShow?.showId != null && shouldStartLoadingData,
    queryKey: ["productsByParent", props.fromShow?.showId],
    onSuccess: data => setProducts(data.collection.filter(p => p.sales == null || (p.sales.totalExVat.amount > 0)).map(p => p.productId)),
  });

  React.useEffect(() => {
    const hasFinishedFetching = [
      bankAccountsApi.isFetching,
      brandsApi.isFetching,
      productListApi.isFetching,
    ].every(f => f === false);

    if (shouldStartLoadingData && hasFinishedFetching) {
      setHasFinishedInitializing(true);
    }
  }, [
    bankAccountsApi.isFetching,
    brandsApi.isFetching,
    productListApi.isFetching,
    shouldStartLoadingData
  ]);

  const hasNoBankAccounts = !bankAccountsApi.isFetching && bankAccountsApi.data.length === 0;

  const onBookkeepingChange = (bookkeeping: server.dto.BookkeepingSmall) => {
    setBookkeeping(bookkeeping);
    setCurrency(bookkeeping.baseCurrency);
    setExchangeRate(1);
    setBankAccount(null);
  }

  const onSave = () => {
    if (!hasFinishedInitializing) return;

    return window.CoreApi.InvoiceV2.Create({
      addresseeCompanyId: addressee.companyId,
      bankAccountId: bankAccount?.bankAccountId,
      bookkeepingId: bookkeeping.bookkeepingId,
      brandId: brand.brandId,
      collectionMethod: collectionMethod.value,
      costCenterId: costCenter?.costCenterId,
      culture: language,
      currency: currency,
      exchangeRate: exchangeRate,
      projectCode: projectCode,
      recipientContactIds: props.fromShow?.promoterContactId ? [props.fromShow.promoterContactId] : [],
      selectedProductIds: products,
      showId: props.fromShow?.showId,
      purchaseOrderNumber: purchaseOrderNumber
    });
  }

  const redirectToInvoiceV2 = (invoiceId: number) => {
    window.location.href = routes.invoiceV2_Detail(invoiceId).absolute;
  }

  const redirectToLegacy = () => {
    if (props.fromShow?.showId) {
      window.location.href = routes.show_NewInvoice(props.fromShow.showId).absolute + (props.qs ? `?${props.qs}` : "");
    } else {
      window.location.href = routes.invoiceV1_New().absolute + (props.qs ? `?${props.qs}` : "");
    }
  }

  const modals = useModals();

  const onAddresseeChange = (company: server.dto.CompanySmall) => {
    if (company) {
      modals.displayFinancialRiskModal(company.companyId);
    }
    setAddressee(company);
  }

  return <ModernOrLegacy
    modalTitle={site.currentEnvironment.invoice.newLabel}
    useModernOrLegacyHook={props.modernOrLegacyHook}
    redirectToLegacy={redirectToLegacy}
    isLoadingMinimalCreationScreen={!hasFinishedInitializing}
    newLabel={site.currentEnvironment.invoice.newLabel}
    submit={onSave}
    redirectToModernPage={redirectToInvoiceV2}
    editModalWidth="small"
    sections={[{
      key: "invoice",
      type: "regular",
      title: site.currentEnvironment.invoice.newLabel,
      fields: [{
        key: "bookkeeping",
        label: RESX.Bookkeeping.resxBookkeeping,
        renderEditNew: () => (
          <SelectBookkeeping
            value={bookkeeping}
            onChange={onBookkeepingChange}
            options={props.bookkeepings}
          />
        )
      }, {
        key: "currency",
        label: RESX.Financial.Currency,
        hidden: !bookkeeping,
        renderEditNew: () => (
          <CurrencyAndExchangeRatePicker
            validatorKey="currencyAndExchangeRate"
            bookkeeping={bookkeeping}
            currency={currency}
            exchangeRate={exchangeRate}
            onChange={v => {
              setCurrency(v.currency);
              setExchangeRate(v.exchangeRate);
            }}
            onEsc={() => { }}
            onEnter={() => { }}
          />
        )
      }, {
        key: "bankAccount",
        label: RESX.BankAccount.resxBankAccount,
        hidden: !bookkeeping,
        renderEditNew: () => (
          hasNoBankAccounts
            ? <div style={{ marginTop: 4, marginBottom: 4 }}>
              <NoBankAccountsWarning />
            </div>
            : <SelectBankAccount
              value={bankAccount}
              onChange={setBankAccount}
              options={bankAccountsApi.data}
            />
        )
      }, {
        key: "paymentMethod",
        hidden: !site.permissions.canAccessAdminOne,
        label: RESX.Payment.PaymentMethod,
        renderEditNew: () => (
          <InvoiceV2CollectionMethodDropdown
            value={collectionMethod}
            onChange={setCollectionMethod}
          />
        )
      }, {
        key: "addressee",
        label: RESX.Document.Addressee,

        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <CompanyModalPicker
              modalTitle={RESX.Document.Addressee}
              hideEditButton
              addressType="administrative"
              canCreateNew={site.permissions.addressBook.canCreate}
              afterCreate="closeSidebar"
              nonInPageEditing={{
                onChange: onAddresseeChange,
                value: addressee,
                validatorKey: "addresseeCompany",
                required: true,
              }}
            />
          </div>
        )
      }, {
        key: "stripe",
        label: "Stripe",
        hidden: collectionMethod.value !== "stripeOffSession",
        renderEditNew: () => (
          <div>Stripe</div>
        )
      }, {
        key: "costCenter",
        label: RESX.CostCenter.resxCostCenter,
        hidden: !site.currentEnvironment.hasCostCenters,
        renderEditNew: () => (
          <div className="main-section-template-fields-padding">
            <CostCenterModalPicker
              canCreateNew={false}
              nonInPageEditing={{
                onChange: setCostCenter,
                value: costCenter,
                validatorKey: "costCenter",
                required: true,
              }}
            />
          </div>
        )
      }, {
        key: "projectCode",
        label: RESX.Show.ProjectCode,
        hidden: site.currentEnvironment.projectCode === "hidden",
        renderEditNew: () => (
          <div className={classNames({ "main-section-template-fields-padding": site.currentEnvironment.projectCode === "displayBusinessCentral" })}>
            <ProjectCodeInput
              value={projectCode}
              onChange={setProjectCode}
              canCreateNew={false}
            />
          </div>
        )
      }, {
        key: "brand",
        label: RESX.Branding.resxBranding,
        hidden: hasOnlyOneBrand,
        renderEditNew: () => (
          <SelectBrand
            value={brand}
            onChange={setBrand}
            options={brandsApi.data}
          />
        )
      }, {
        key: "purchaseOrderNumber",
        label: RESX.Invoice.PurchaseOrderNumber,
        hidden: !site.currentEnvironment.purchaseOrderNumber,
        renderEditNew: () => (
          <OptionalTextInput
            value={purchaseOrderNumber}
            onChange={setPurchaseOrderNumber}
            maxLength={serverReference.constants.document.maxPurchaseOrderNumberLength}
          />
        )
      }, {
        key: "language",
        label: RESX.Invoice.DocumentLanguage,
        renderEditNew: () => (
          <CulturesTemplateDropdown
            value={language}
            onChange={setLanguage}
          />
        )
      }, {
        key: "products",
        label: RESX.Product.Products,
        hidden: !props.fromShow?.showId,
        renderEditNew: () => <ProductCheckboxList
          productsApi={productListApi}
          selectedProducts={products}
          setSelectedProducts={setProducts}
        />
      }]
    }]}
  />
}

export default CreateInvoice;